import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Card, Stack, Tab, Tabs, Theme, Typography, useMediaQuery } from '@mui/material';
import BigNumber from 'bignumber.js';
import { userModel } from 'entities/user';
import { userActions } from 'entities/user/model';
import { TokenInput } from 'features';
import {
  BORDER_RADIUS_L,
  BORDER_RADIUS_M,
  checkRequestStatusEquality,
  COLOR_NEUTRAL_ULTRALIGHT,
  COLOR_WH,
  ContractsNames,
  ContractsService,
  fromDecimals,
  Icon,
  RequestStatus,
  uiSelector,
  useShallowSelector,
  useValidateInputField,
  validateOnlyNumbers,
  ValidationTypes,
} from 'shared';
import MogulLogo from 'shared/assets/images/mogul-logo.png';
import { useWeb3Provider } from 'shared/config/wallet-connect';
import { TimerWithClaim } from 'widgets';
import * as dateFns from 'date-fns';

const VESTING_RATE_TAB_DENOMINATOR = 1000;

interface VestingDaysTabProps {
  seconds: number;
  rate: number;
}

const VestingDaysTab = ({ seconds, rate }: VestingDaysTabProps) => {
  const days = Math.floor(seconds / 60 / 60 / 24);

  return (
    <Stack direction="row" spacing={1}>
      <Typography component="span" variant="caption" className="bold gray" noWrap>
        {days || seconds} {days ? 'days' : 'sec'}
      </Typography>
      <Typography component="span" variant="caption" className="gray" noWrap>
        {Math.floor(rate * 100 - 100)}%
      </Typography>
    </Stack>
  );
};

interface VestingDaysSelectProps {
  vestingRates: [number, number][];
  vestingVariant: number | null;
  handleVestingVariantSelect: (v: number) => void;
  isSmallScreen: boolean;
}

const VestingDaysSelect = ({
  vestingRates,
  vestingVariant,
  handleVestingVariantSelect,
  isSmallScreen,
}: VestingDaysSelectProps) => {
  const vestingSeconds = useMemo(() => vestingRates?.[vestingVariant ?? 3]?.[0], [vestingRates, vestingVariant]);
  const reversedVestingRates = useMemo(() => vestingRates.toReversed(), [vestingRates]);

  return (
    <Tabs
      value={reversedVestingRates.map((d) => d[0]).includes(vestingSeconds) && vestingSeconds && vestingVariant}
      onChange={(_, value) => handleVestingVariantSelect(vestingRates.findIndex(([days]) => days === value))}
      variant="fullWidth"
      sx={{
        py: 1,
        '.MuiTabs-flexContainer': {
          display: 'grid',
          gridTemplateColumns: isSmallScreen ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: '8px',
        },
        '.MuiTab-root': {
          padding: 1,
          margin: 0,
        },
      }}
      slotProps={{}}
    >
      {reversedVestingRates.map(([seconds, rate], index) => {
        // const isDisabled = index > 1;
        const isDisabled = true;

        return (
          <Tab
            value={seconds}
            key={seconds}
            label={<VestingDaysTab seconds={seconds} rate={rate / VESTING_RATE_TAB_DENOMINATOR} />}
            disabled={isDisabled}
            sx={(theme) => ({
              '&.Mui-selected': {
                background: theme.themeColors.colorTextFieldTokenBackground,
                '& .MuiTypography-root': {
                  color: '#FFFFFF',
                },
              },
              '&.Mui-disabled': {
                opacity: 0.5,
              },
            })}
          />
        );
      })}
    </Tabs>
  );
};

export const Exchange = () => {
  const { vestingRates, finish, startTime, swapRateDenominator } = useShallowSelector(
    userModel.selectors.selectVestingSwapInfo,
  );
  const { chainType, network } = useShallowSelector(userModel.selectors.selectUserWeb3Info);
  const tokens = useShallowSelector(userModel.selectors.selectTokenBalances);

  const [isError, setIsError] = useState('');
  const [amountFrom, handleChangeAmountFrom, setAmountFrom] = useValidateInputField({ type: ValidationTypes.number });
  const [amountTo, handleChangeAmountTo, setAmountTo] = useValidateInputField({ type: ValidationTypes.number });
  const [vestingVariant, setVestingVariant] = useState<number | null>(null);
  const web3Provider = useWeb3Provider();
  const dispatch = useDispatch();
  const uiState = useShallowSelector(uiSelector);
  const isSwaping = checkRequestStatusEquality(uiState[userActions.swap?.type], RequestStatus.REQUEST);
  const isSwapSuccess = checkRequestStatusEquality(
    uiState[userActions.swap?.type],
    RequestStatus.SUCCESS || RequestStatus.ERROR,
  );

  // const ratio = useMemo(() => vestingRates?.[vestingVariant ?? 3]?.[1], [vestingRates, vestingVariant]);
  const ratio = 0;
  const vestingSeconds = useMemo(() => vestingRates?.[vestingVariant ?? 3]?.[0], [vestingRates, vestingVariant]);

  const starsV1Token =
    tokens[ContractsService.getContractData(ContractsNames.StarsV1, network, chainType)?.address?.toLowerCase()];
  const starsV2Token =
    tokens[ContractsService.getContractData(ContractsNames.StarsV2, network, chainType)?.address?.toLowerCase()];

  // const isSwapDisabled = useMemo(() => !+amountFrom || isSwaping || !!isError, [amountFrom, isError, isSwaping]);
  const isSwapDisabled = true;

  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const updateAmountTo = (value: string | number, amountRatio: number) => {
    if (value && validateOnlyNumbers({ value: `${value}` }) && amountRatio) {
      setAmountTo(
        BigNumber(
          BigNumber(value).times(amountRatio).div(swapRateDenominator).toFixed(Number(starsV2Token?.decimals), 1),
        ).toString(10),
      );
    } else setAmountTo('');
  };

  const handleChangeFromInput = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    handleChangeAmountFrom(e);
    updateAmountTo(e.target.value, ratio);
  };

  const handleChangeToInput = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    handleChangeAmountTo(e);
    if (e.target.value && validateOnlyNumbers({ value: e.target.value }) && ratio) {
      setAmountFrom(
        BigNumber(
          BigNumber(e.target.value).div(ratio).times(swapRateDenominator).toFixed(Number(starsV1Token?.decimals), 1),
        ).toString(10),
      );
    } else setAmountFrom('');
  };

  const handleMaxClick = () => {
    if (starsV1Token?.balance) {
      const value = BigNumber(starsV1Token.balance)
        .div(10 ** +(starsV1Token?.decimals || 18))
        .toFixed();
      handleChangeFromInput({ target: { value } } as ChangeEvent<HTMLTextAreaElement | HTMLInputElement>);
    }
  };

  const handleVestingVariantSelect = useCallback(
    (value: number) => {
      setVestingVariant(value);
      updateAmountTo(amountFrom, vestingRates[value][1]);
    },
    [amountFrom, setVestingVariant, updateAmountTo],
  );

  const handleSwap = useCallback(() => {
    dispatch(
      userModel.userActions.swap({
        web3Provider,
        amount: amountFrom,
        decimals: starsV1Token?.decimals,
        lockType: vestingVariant ?? 3,
      }),
    );
  }, [dispatch, amountFrom, starsV1Token?.decimals, web3Provider]);

  useEffect(() => {
    if (
      BigNumber(amountFrom)
        .times(10 ** +(starsV1Token?.decimals || 18))
        .isGreaterThan(starsV1Token?.balance || 0)
    )
      setIsError('Insufficient balance');
    else setIsError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountFrom]);

  useEffect(() => {
    if (isSwapSuccess) {
      setAmountFrom('');
      setAmountTo('');
    }
  }, [isSwapSuccess, setAmountFrom, setAmountTo]);

  return (
    <Card sx={{ maxWidth: '100%', mt: 3 }}>
      <Stack spacing={1}>
        <Stack
          spacing={1}
          sx={{
            position: 'relative',
          }}
        >
          <TokenInput
            label="You pay"
            value={amountFrom}
            onChange={handleChangeFromInput}
            tokenIcon={<Icon type="star" size={32} />}
            tokenBalance={fromDecimals(starsV1Token?.balance, starsV1Token?.decimals, 2)}
            tokenSymbol={starsV1Token?.symbol}
            error={isError}
            onMaxClick={handleMaxClick}
          />
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              width: '36px',
              height: '36px',
              background: COLOR_NEUTRAL_ULTRALIGHT,
              border: `4px solid ${COLOR_WH}`,
              borderRadius: BORDER_RADIUS_M,
              position: 'absolute',
              top: `88px`,
              left: 'calc(50% - 18px)',
              zIndex: 10,
              mt: '0 !important',
            }}
          >
            <Icon type="arrow" width={10} height={14} />
          </Stack>
          <TokenInput
            label="You receive"
            value={amountTo}
            onChange={handleChangeToInput}
            tokenIcon={<Box component="img" src={MogulLogo} width={32} height={32} />}
            tokenSymbol={starsV2Token?.symbol}
            helperText={`ratio x${ratio / swapRateDenominator || '...'}`}
          />
          <VestingDaysSelect
            vestingRates={vestingRates}
            vestingVariant={vestingVariant}
            handleVestingVariantSelect={handleVestingVariantSelect}
            isSmallScreen={isSmallScreen}
          />
          {startTime ? (
            <Stack
              spacing={1}
              alignItems="center"
              sx={(theme) => ({
                border: `1px solid ${theme.themeColors.colorTimerBorder}`,
                py: '16px',
                px: '40px',
                borderRadius: BORDER_RADIUS_L,
              })}
            >
              <Typography className="gray" textAlign="center">
                You will be able to claim Mogul tokens starting from{' '}
                <b>
                  {finish
                    ? dateFns.format(
                        dateFns.add(dateFns.fromUnixTime(finish), { seconds: vestingSeconds }),
                        'dd MMMM yyyy',
                      )
                    : ''}
                </b>
              </Typography>
            </Stack>
          ) : null}
        </Stack>
        <TimerWithClaim
          isSwaping={isSwaping}
          swapTime={+finish}
          isSwapDisabled={isSwapDisabled}
          startTime={startTime}
          onSwap={handleSwap}
        />
      </Stack>
    </Card>
  );
};
